import React, { useRef, useState, useEffect, ReactElement, CSSProperties } from "react";
import styles from "./Modal.module.css";
import FocusLock from "react-focus-lock";
import Button from "../Button/Button";
import Backdrop from '../Backdrop/Backdrop';

interface ModalProps {
  cancelText?: string;
  confirmText?: string;
  show: boolean;
  labelledBy: string;
  describedBy: string;
  id?: string;
  children: React.ReactNode;
  type?: "confirm";
  shards?: Array<any>;
  cancelButtonColor?: "red" | "blue" | "green" | "grey"
  confirmButtonColor?: "red" | "blue" | "green" | "grey"
  cancelButtonDisabled?: boolean;
  confirmButtonDisabled?: boolean;
  clickBackdropToDismiss?: boolean;
  hideControls?: boolean;
  hideBackdrop?: boolean;
  /**
   * Pass an id of the element to be focused upon close
   */
  focusOnClose?: string;
  onConfirm?: (arg0: any) => void;
  onClose: () => void;
  footerStyle?: CSSProperties;
  cancelBtnStyle?: CSSProperties;
  confirmBtnStyle?: CSSProperties;
  style?: CSSProperties;
}

const Modal: React.FC<ModalProps> = ({
  cancelText = "Cancel",
  confirmText = "Confirm",
  show = "false",
  labelledBy,
  describedBy,
  id,
  cancelButtonColor,
  confirmButtonColor,
  children,
  type,
  shards,
  clickBackdropToDismiss = true,
  hideControls = false,
  hideBackdrop = false,
  cancelButtonDisabled = false,
  confirmButtonDisabled = false,
  focusOnClose,
  onConfirm,
  onClose,
  footerStyle,
  cancelBtnStyle,
  confirmBtnStyle,
  style,
}): ReactElement => {
  const [closing, setClosing] = useState(false)
  const [left, setLeft] = useState(0)
  const [top, setTop] = useState(window.pageYOffset || document.documentElement.scrollTop)
  const getNewTop = () => {
    setTop((window.pageYOffset || document.documentElement.scrollTop) + 20)
  }
  document.addEventListener('scroll', getNewTop);

  const modalRef = useRef(null);
  const closeModal = () => {
    setClosing(true)
      setTimeout(function () {
    onClose();
    focusOnClose && document.getElementById(focusOnClose).focus()
    setClosing(false)
    }, 390);
  
  };

  useEffect(() => {
    function keyListener(e: KeyboardEvent) {
      const listener = keyListenersMap.get(e.keyCode);
      return listener && listener();
    }
    if(show){
      document.addEventListener("keydown", keyListener);
    } else {document.removeEventListener("keydown", keyListener)}
  }, [show]);

  useEffect(() => {
    function setModalLeft() {
      if(modalRef.current){
      const left = (window.innerWidth - modalRef.current.scrollWidth)/2
      setLeft(left);
      }
    }
    if(show && modalRef.current){
      setModalLeft();
      window.addEventListener('resize', setModalLeft);
    }
  }, [show])

  const keyListenersMap = new Map([[27, closeModal]]);

  return show ? (
    <FocusLock autoFocus={false} shards={shards}>
      {!hideBackdrop && <Backdrop close={clickBackdropToDismiss ? closeModal : null} />}
      <div
        className={`${styles.Modal} ${closing ? styles.closing : null} `}
        role="dialog"
        aria-modal="true"
        aria-describedby={describedBy}
        aria-labelledby={labelledBy}
        id={id}
        ref={modalRef}
        style={{left, top, ...style }}
      >
        <div className={styles.Modal__body}>{children}</div>
        {!hideControls && (
          <div className={styles.Modal__footer} style={footerStyle}>
            <Button disabled={cancelButtonDisabled} text={cancelText} name={cancelText} onClick={closeModal} color={cancelButtonColor || "grey"} style={{margin: 0, ...cancelBtnStyle}}/>
            {type === "confirm" && (
              <Button disabled={confirmButtonDisabled} text={confirmText} name={confirmText} color={confirmButtonColor || "blue"} onClick={onConfirm} style={{margin: "0 0 0 2rem", ...confirmBtnStyle}}/>
            )}
          </div>
        )}
      </div>
    </FocusLock>
  ) : (
    <></>
  );
};

export default Modal;
